import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import ImageUpload from './ImageUpload';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSave, FiX } = FiIcons;

const SegnalazioneForm = ({ 
  editingSegnalazione = null, 
  currentUser, 
  createSegnalazione, 
  updateSegnalazione, 
  setCurrentView 
}) => {
  const { isDark } = useTheme();
  const [formData, setFormData] = React.useState({
    indirizzo: '',
    mq: '',
    nome_proprietario: '',
    telefono_proprietario: '',
    tipo_immobile: '',
    stato_immobile: '',
    note: '',
    stato_segnalazione: 'In attesa'
  });
  const [images, setImages] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (editingSegnalazione) {
      setFormData({
        indirizzo: editingSegnalazione.indirizzo || '',
        mq: editingSegnalazione.mq || '',
        nome_proprietario: editingSegnalazione.nome_proprietario || '',
        telefono_proprietario: editingSegnalazione.telefono_proprietario || '',
        tipo_immobile: editingSegnalazione.tipo_immobile || '',
        stato_immobile: editingSegnalazione.stato_immobile || '',
        note: editingSegnalazione.note || '',
        stato_segnalazione: editingSegnalazione.stato_segnalazione || 'In attesa'
      });
      setImages(editingSegnalazione.images || []);
    }
  }, [editingSegnalazione]);

  const validateForm = () => {
    if (!formData.indirizzo.trim()) {
      setError('L\'indirizzo è obbligatorio');
      return false;
    }
    if (!formData.mq || parseInt(formData.mq) <= 0) {
      setError('I metri quadri devono essere un numero positivo');
      return false;
    }
    if (!formData.nome_proprietario.trim()) {
      setError('Il nome del proprietario è obbligatorio');
      return false;
    }
    if (!formData.telefono_proprietario.trim()) {
      setError('Il telefono del proprietario è obbligatorio');
      return false;
    }
    if (!formData.tipo_immobile) {
      setError('Seleziona il tipo di immobile');
      return false;
    }
    if (!formData.stato_immobile) {
      setError('Seleziona lo stato dell\'immobile');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      console.log('Submitting form with data:', formData);
      console.log('Images:', images);

      const segnalazioneData = {
        ...formData,
        mq: parseInt(formData.mq),
        images: images,
        // Mantieni le note admin esistenti se stai modificando
        note_admin: editingSegnalazione ? editingSegnalazione.note_admin : undefined
      };

      // IMPORTANTE: Gestisci user_id diversamente per creazione vs modifica
      if (editingSegnalazione) {
        // Per le modifiche, NON includere user_id per preservare il proprietario originale
        // Il backend si occuperà di non modificare user_id
        console.log('Updating existing segnalazione - preserving original owner');
      } else {
        // Solo per nuove segnalazioni, imposta il user_id dell'utente corrente
        segnalazioneData.user_id = currentUser.id;
        console.log('Creating new segnalazione for user:', currentUser.id);
      }

      console.log('Final segnalazione data:', segnalazioneData);

      let result;
      if (editingSegnalazione) {
        result = await updateSegnalazione(editingSegnalazione.id, segnalazioneData);
      } else {
        result = await createSegnalazione(segnalazioneData);
      }

      console.log('Operation result:', result);

      if (result.error) {
        console.error('Error saving segnalazione:', result.error);
        setError(result.error.message || 'Errore durante il salvataggio della segnalazione');
      } else {
        // Successo - torna alla dashboard
        setCurrentView(currentUser.role === 'admin' ? 'admin-dashboard' : 'segnalatore-dashboard');
      }
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      setError('Errore durante il salvataggio della segnalazione');
    } finally {
      setLoading(false);
    }
  };

  const isEditingMode = !!editingSegnalazione;
  const isAdmin = currentUser.role === 'admin';

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'} transition-colors`}>
      <div className="container mx-auto px-4 py-8">
        <div className={`max-w-4xl mx-auto ${isDark ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-xl overflow-hidden`}>
          {/* Header */}
          <div className={`${isDark ? 'bg-gradient-to-r from-blue-800 to-blue-900' : 'bg-gradient-to-r from-blue-600 to-blue-700'} text-white p-6`}>
            <h2 className="text-2xl font-bold">
              {isEditingMode ? 'Modifica Segnalazione' : 'Nuova Segnalazione Immobiliare'}
            </h2>
            <p className="text-blue-100 mt-1">
              {isEditingMode ? 'Aggiorna i dettagli della segnalazione' : 'Compila tutti i campi per creare una segnalazione completa'}
            </p>
            {isEditingMode && isAdmin && (
              <div className="mt-3 space-y-2">
                <div className="px-3 py-1 bg-blue-700 rounded-full text-sm inline-block">
                  🛠️ Modalità Amministratore - Controllo Completo
                </div>
                <div className="px-3 py-1 bg-green-700 rounded-full text-xs inline-block ml-2">
                  👤 Proprietario originale preservato
                </div>
              </div>
            )}
            {isEditingMode && editingSegnalazione?.users_hs2025 && (
              <div className="mt-3 p-3 bg-blue-700/50 rounded-lg">
                <p className="text-sm">
                  📝 <strong>Segnalazione di:</strong> {editingSegnalazione.users_hs2025.email}
                </p>
                <p className="text-xs text-blue-200 mt-1">
                  Questa segnalazione rimarrà associata al segnalatore originale
                </p>
              </div>
            )}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6">
            {error && (
              <div className="mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Informazioni Immobile */}
              <div className="space-y-6">
                <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-800'} border-b pb-2`}>
                  📍 Informazioni Immobile
                </h3>

                <div>
                  <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Indirizzo Completo *
                  </label>
                  <input
                    type="text"
                    value={formData.indirizzo}
                    onChange={(e) => setFormData({ ...formData, indirizzo: e.target.value })}
                    className={`w-full px-4 py-3 rounded-lg border-2 transition-colors focus:outline-none ${
                      isDark 
                        ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                        : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-blue-500'
                    }`}
                    placeholder="Via Roma 123, Milano (MI)"
                    required
                    disabled={loading}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Metri Quadri *
                    </label>
                    <input
                      type="number"
                      value={formData.mq}
                      onChange={(e) => setFormData({ ...formData, mq: e.target.value })}
                      className={`w-full px-4 py-3 rounded-lg border-2 transition-colors focus:outline-none ${
                        isDark 
                          ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                          : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-blue-500'
                      }`}
                      placeholder="85"
                      min="1"
                      required
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Tipo Immobile *
                    </label>
                    <select
                      value={formData.tipo_immobile}
                      onChange={(e) => setFormData({ ...formData, tipo_immobile: e.target.value })}
                      className={`w-full px-4 py-3 rounded-lg border-2 transition-colors focus:outline-none ${
                        isDark 
                          ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                          : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-blue-500'
                      }`}
                      required
                      disabled={loading}
                    >
                      <option value="">Seleziona tipo</option>
                      <option value="Appartamento">🏠 Appartamento</option>
                      <option value="Villa">🏡 Villa</option>
                      <option value="Villetta">🏘️ Villetta</option>
                      <option value="Attico">🏢 Attico</option>
                      <option value="Loft">🏭 Loft</option>
                      <option value="Altro">🏗️ Altro</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Stato Immobile *
                  </label>
                  <select
                    value={formData.stato_immobile}
                    onChange={(e) => setFormData({ ...formData, stato_immobile: e.target.value })}
                    className={`w-full px-4 py-3 rounded-lg border-2 transition-colors focus:outline-none ${
                      isDark 
                        ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                        : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-blue-500'
                    }`}
                    required
                    disabled={loading}
                  >
                    <option value="">Seleziona stato</option>
                    <option value="Ottimo">✨ Ottimo</option>
                    <option value="Buono">👍 Buono</option>
                    <option value="Da ristrutturare">🔨 Da ristrutturare</option>
                    <option value="Nuovo">🆕 Nuovo</option>
                  </select>
                </div>
              </div>

              {/* Informazioni Proprietario */}
              <div className="space-y-6">
                <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-800'} border-b pb-2`}>
                  👤 Informazioni Proprietario
                </h3>

                <div>
                  <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Nome Proprietario *
                  </label>
                  <input
                    type="text"
                    value={formData.nome_proprietario}
                    onChange={(e) => setFormData({ ...formData, nome_proprietario: e.target.value })}
                    className={`w-full px-4 py-3 rounded-lg border-2 transition-colors focus:outline-none ${
                      isDark 
                        ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                        : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-blue-500'
                    }`}
                    placeholder="Mario Rossi"
                    required
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Telefono Proprietario *
                  </label>
                  <input
                    type="tel"
                    value={formData.telefono_proprietario}
                    onChange={(e) => setFormData({ ...formData, telefono_proprietario: e.target.value })}
                    className={`w-full px-4 py-3 rounded-lg border-2 transition-colors focus:outline-none ${
                      isDark 
                        ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                        : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-blue-500'
                    }`}
                    placeholder="+39 123 456 7890"
                    required
                    disabled={loading}
                  />
                </div>

                {/* Campo Stato Segnalazione - Solo per Admin */}
                {isAdmin && (
                  <div className={`p-4 rounded-lg border-2 ${
                    isDark ? 'border-blue-600 bg-blue-900/20' : 'border-blue-200 bg-blue-50'
                  }`}>
                    <label className={`block text-sm font-semibold mb-2 ${
                      isDark ? 'text-blue-300' : 'text-blue-700'
                    }`}>
                      🛠️ Stato Segnalazione (Solo Admin)
                    </label>
                    <select
                      value={formData.stato_segnalazione}
                      onChange={(e) => setFormData({ ...formData, stato_segnalazione: e.target.value })}
                      className={`w-full px-4 py-3 rounded-lg border-2 transition-colors focus:outline-none ${
                        isDark 
                          ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                          : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-blue-500'
                      }`}
                      disabled={loading}
                    >
                      <option value="In attesa">⏳ In attesa</option>
                      <option value="Visionato">👁️ Visionato</option>
                      <option value="Incarico preso">✅ Incarico preso</option>
                      <option value="Incarico non preso">❌ Incarico non preso</option>
                      <option value="Venduto">💰 Venduto</option>
                      <option value="Rifiutato">🚫 Rifiutato</option>
                    </select>
                    <p className={`text-xs mt-2 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                      Come amministratore puoi modificare lo stato della segnalazione
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Note e Foto */}
            <div className="mt-8 space-y-6">
              <div>
                <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  📝 Note Aggiuntive
                </label>
                <textarea
                  value={formData.note}
                  onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                  className={`w-full px-4 py-3 rounded-lg border-2 transition-colors focus:outline-none ${
                    isDark 
                      ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500' 
                      : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-blue-500'
                  }`}
                  rows="4"
                  placeholder="Aggiungi dettagli importanti sull'immobile, condizioni particolari, orari per la visita..."
                  disabled={loading}
                />
              </div>

              <div>
                <label className={`block text-sm font-semibold mb-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  📸 Foto Immobile
                </label>
                <ImageUpload 
                  images={images} 
                  onImagesChange={setImages} 
                  maxImages={3} 
                />
              </div>
            </div>

            {/* Actions */}
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 flex items-center justify-center space-x-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                <SafeIcon icon={FiSave} className="w-5 h-5" />
                <span>
                  {loading ? 'Salvando...' : (isEditingMode ? 'Aggiorna' : 'Salva')} Segnalazione
                </span>
              </button>

              <button
                type="button"
                onClick={() => setCurrentView(currentUser.role === 'admin' ? 'admin-dashboard' : 'segnalatore-dashboard')}
                disabled={loading}
                className={`flex-1 flex items-center justify-center space-x-2 px-6 py-3 rounded-lg font-semibold transition-colors ${
                  isDark 
                    ? 'bg-gray-600 text-white hover:bg-gray-500' 
                    : 'bg-gray-500 text-white hover:bg-gray-600'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <SafeIcon icon={FiX} className="w-5 h-5" />
                <span>Annulla</span>
              </button>
            </div>

            {/* Info per Admin */}
            {isEditingMode && isAdmin && (
              <div className={`mt-6 p-4 rounded-lg ${
                isDark ? 'bg-green-900/20 border border-green-600' : 'bg-green-50 border border-green-200'
              }`}>
                <h4 className={`font-semibold mb-2 ${isDark ? 'text-green-300' : 'text-green-700'}`}>
                  🔒 Protezione Proprietà Segnalazione
                </h4>
                <ul className={`text-sm space-y-1 ${isDark ? 'text-green-200' : 'text-green-600'}`}>
                  <li>• ✅ La segnalazione rimane associata al segnalatore originale</li>
                  <li>• ✅ Puoi modificare tutti i dettagli dell'immobile</li>
                  <li>• ✅ Puoi aggiornare lo stato e aggiungere note admin</li>
                  <li>• ✅ Il proprietario originale non viene mai modificato</li>
                  <li>• ✅ Protezione automatica dell'ownership durante gli aggiornamenti</li>
                </ul>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default SegnalazioneForm;